import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

export interface SessionAttendee {
  id: string
  name: string
  email: string | null
  phone: string | null
  position: string | null
  attended: boolean
  applicationId: string
  sessionId: string
  bookingId: string
}

export const useSessionAttendees = (sessionId: string | null) => {
  const fetchAttendees = async (): Promise<SessionAttendee[]> => {
    if (!sessionId) return []
    try {
      // Query session_bookings with joins to get applicant information
      const { data, error } = await supabase
        .from("session_bookings")
        .select(
          `
          id,
          session_id,
          application_id,
          attended,
          applications!inner(
            id,
            applicant:applicant_id(
              id,
              first_name,
              last_name,
              email,
              phone_number
            ),
            assessments(*)
          )
        `,
        )
        .eq("session_id", sessionId)
        .is("cancelled_at", null)

      if (error) {
        console.error("Error fetching session attendees:", error)
        throw error
      }
      // Transform the data to match the SessionAttendee interface
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const attendees: SessionAttendee[] = data.map((booking: any) => {
        const application = booking.applications
        const applicant = application.applicant

        return {
          id: application.id,
          name: `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim(),
          email: applicant.email,
          phone: applicant.phone_number,
          position: applicant.job?.title || null,
          attended: booking.attended,
          applicationId: application.id,
          sessionId: booking.session_id,
          bookingId: booking.id,
          orientationQuizResult:
            application.assessments
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .find((assessment: any) => assessment.type === "orientation-quiz")
              ?.score?.toFixed(2) || 0,
        }
      })

      return attendees
    } catch (error) {
      console.error("Error in useSessionAttendees:", error)
      return []
    }
  }

  return useQuery({
    queryKey: ["sessionAttendees", sessionId],
    queryFn: fetchAttendees,
    enabled: !!sessionId,
  })
}
